/**
 * §B-1 store editor — Drawer create/edit form. Secrets (access token + webhook
 * secret) are WRITE-ONLY: on create both are required; on edit they show a
 * present/missing status and a "leave blank to keep current" input that only
 * rotates when filled. Saves via the ADMIN API with optimistic concurrency.
 */
import { useState } from "react";
import { ApiError, api, type AdminStore, type StoreCreateInput, type StoreUpdateInput } from "../../../lib/api.js";
import type { StoreRegion } from "@cairn/shared";
import { Drawer, Field, Input, Select, Button, StateChip, useToast } from "../../../components/ui/index.js";

export function StoreEditor({ store, onClose, onSaved }: { store: AdminStore | null; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const isNew = store === null;

  const [name, setName] = useState(store?.name ?? "");
  const [shopDomain, setShopDomain] = useState(store?.shopDomain ?? "");
  const [region, setRegion] = useState<StoreRegion>(store?.region ?? "EU");
  const [currency, setCurrency] = useState(store?.currency ?? "EUR");
  const [locationId, setLocationId] = useState(store?.locationId ?? "");
  const [apiVersion, setApiVersion] = useState(store?.apiVersion ?? "");
  const [active, setActive] = useState(store?.active ?? true);
  // "" = inherit the global default (Settings.shortagePolicy); else a per-store override.
  const [shortagePolicy, setShortagePolicy] = useState<string>(store?.shortagePolicy ?? "");
  const [autoCorrect, setAutoCorrect] = useState(store?.autoCorrectInventory ?? false);
  const [packedTag, setPackedTag] = useState(store?.packedTag ?? "");
  const [accessToken, setAccessToken] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim() || !shopDomain.trim()) {
      toast("Name and shop domain are required.", "danger");
      return;
    }
    if (!/^[A-Za-z]{3}$/.test(currency.trim())) {
      toast("Currency must be a 3-letter code (e.g. EUR).", "danger");
      return;
    }
    if (isNew && (!accessToken || !webhookSecret)) {
      toast("Access token and webhook secret are required for a new store.", "danger");
      return;
    }
    const base = {
      name: name.trim(),
      shopDomain: shopDomain.trim(),
      region,
      defaultCurrency: currency.trim().toUpperCase(),
      locationId: locationId.trim() || null,
      apiVersion: apiVersion.trim() || null,
      active,
      shortagePolicy: shortagePolicy || null, // "" → inherit global default
      autoCorrectInventory: autoCorrect,
      packedTag: packedTag.trim() || null,
    };
    setSaving(true);
    try {
      if (isNew) {
        await api.admin.createStore({ ...base, accessToken, webhookSecret } as StoreCreateInput);
      } else {
        const input: StoreUpdateInput = { ...base };
        if (accessToken) input.accessToken = accessToken;
        if (webhookSecret) input.webhookSecret = webhookSecret;
        await api.admin.updateStore(store.id, input, store.updatedAt);
      }
      toast(isNew ? "Store created" : "Store saved", "success");
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const code = (e.detail as { code?: string } | undefined)?.code;
        toast(code === "CONFLICT" && isNew ? "A store with that domain already exists." : "This store changed since you opened it — reloading.", "danger");
        if (!isNew) onSaved();
      } else if (e instanceof ApiError && e.status === 400) {
        toast((e.detail as { error?: string } | undefined)?.error ?? "Invalid store.", "danger");
      } else if (e instanceof ApiError && e.status === 403) {
        toast("Admin role required.", "danger");
      } else {
        toast("Couldn’t save the store.", "danger");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      onClose={onClose}
      ariaLabel={isNew ? "New store" : `Edit ${store.name}`}
      title={isNew ? "Connect a store" : "Edit store"}
      meta={<span className="muted">Secrets are stored encrypted and never shown again.</span>}
    >
      <div className="rule-form">
        <div className="rule-form__row2">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Alpine Nation EU" autoFocus />
          </Field>
          <Field label="Shop domain">
            <Input value={shopDomain} onChange={(e) => setShopDomain(e.target.value)} placeholder="alpine-eu.myshopify.com" />
          </Field>
        </div>

        <div className="rule-form__row2">
          <Field label="Region">
            <Select ariaLabel="Region" value={region} onChange={(v) => setRegion(v as StoreRegion)} options={[{ value: "EU", label: "EU" }, { value: "US", label: "US" }, { value: "CA", label: "CA" }]} />
          </Field>
          <Field label="Currency">
            <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} placeholder="EUR" />
          </Field>
        </div>

        <div className="rule-form__row2">
          <Field label="Status">
            <label className="rule-form__toggle">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              <span>{active ? "Active" : "Inactive"}</span>
            </label>
          </Field>
          <Field label="Location id (optional)">
            <Input value={locationId} onChange={(e) => setLocationId(e.target.value)} placeholder="gid://shopify/Location/123…" />
          </Field>
        </div>

        <div className="rule-form__row2">
          <Field label="API version (optional)">
            <Input value={apiVersion} onChange={(e) => setApiVersion(e.target.value)} placeholder="2026-04 (blank = default)" />
          </Field>
          <Field label="Packed tag (optional)">
            <Input value={packedTag} onChange={(e) => setPackedTag(e.target.value)} placeholder="cairn-packed" />
          </Field>
        </div>

        <fieldset className="secret-group">
          <legend>Fulfilment</legend>
          <div className="rule-form__row2">
            <Field label="Shortage policy" hint="When an item is short. Default is set in App settings.">
              <Select
                ariaLabel="Shortage policy"
                value={shortagePolicy}
                onChange={setShortagePolicy}
                options={[{ value: "", label: "Use global default" }, { value: "HOLD_WHOLE", label: "Hold whole order" }, { value: "SHIP_PARTIAL", label: "Ship partial" }]}
              />
            </Field>
            <Field label="Auto-correct inventory">
              <label className="rule-form__toggle">
                <input type="checkbox" checked={autoCorrect} onChange={(e) => setAutoCorrect(e.target.checked)} />
                <span>{autoCorrect ? "On" : "Off"}</span>
              </label>
            </Field>
          </div>
        </fieldset>

        <fieldset className="secret-group">
          <legend>Credentials {!isNew && <span className="muted">· encrypted · leave blank to keep</span>}</legend>
          <Field label="Admin API access token">
            {!isNew && store && (
              <span className="secret-status">
                <StateChip on={store.accessTokenStatus === "present"} onLabel="set" offLabel="missing" />
              </span>
            )}
            <Input type="password" value={accessToken} onChange={(e) => setAccessToken(e.target.value)} placeholder={isNew ? "shpat_…" : "Leave blank to keep current"} autoComplete="off" />
          </Field>
          <Field label="Webhook signing secret">
            {!isNew && store && (
              <span className="secret-status">
                <StateChip on={store.webhookSecretStatus === "present"} onLabel="set" offLabel="missing" />
              </span>
            )}
            <Input type="password" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} placeholder={isNew ? "whsec_…" : "Leave blank to keep current"} autoComplete="off" />
          </Field>
        </fieldset>

        <div className="rule-form__actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon="check" onClick={() => void save()} disabled={saving}>
            {saving ? "Saving…" : isNew ? "Connect store" : "Save changes"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
